import { useCallback, useMemo } from 'react';

import { useDispatch } from 'react-redux';

import { updateAction } from '@/redux/nodes/actions';
import { AuthSubAgentConfig, SubAgentAction } from '@/shared/types/action';
import { AuthConfig } from '@/shared/types/node';

/**
 * Adapts a `sub_agent` action carrying an auth config to the existing
 * `AuthConfig` / `onUpdate(Partial<AuthConfig>)` contract used by all of the
 * AuthPage* panels. The discriminator (`type: 'auth'`) is stripped on read and
 * re-attached on write so the panels remain unaware of the wrapper shape.
 */
export const useAuthSubAgentConfig = (actionId: string, action: SubAgentAction) => {
  const dispatch = useDispatch();
  const config = action.sub_agent_config as AuthSubAgentConfig;

  const authConfig: AuthConfig = useMemo(() => {
    const { type: _t, ...rest } = config;
    return rest;
  }, [config]);

  const onUpdate = useCallback(
    (updates: Partial<AuthConfig>) => {
      const nextConfig: AuthSubAgentConfig = { type: 'auth', ...authConfig, ...updates };
      dispatch(updateAction({ actionId, action: { ...action, sub_agent_config: nextConfig } }));
    },
    [action, actionId, authConfig, dispatch]
  );

  return { authConfig, onUpdate };
};
